import React, { useMemo, useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, CheckIcon, InfoIcon, ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import { Product, ProductOption, CartItemType } from '../types/types';
import { BOWL_OPTIONS, INGREDIENT_IMAGES, MENU_PRODUCTS } from '../data/menuData';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
interface ProductCustomizerProps {
  product: Product;
  onClose: () => void;
  onSave: (item: CartItemType) => void;
  initialItem?: CartItemType;
}
export function ProductCustomizer({
  product,
  onClose,
  onSave,
  initialItem
}: ProductCustomizerProps) {
  const isSandwichOrWrap = product.type === 'sandwich' || product.type === 'wrap';
  const isSnackBowl = product.type === 'snack_bowl';
  const {
    addItem
  } = useCart();
  const {
    t,
    formatPrice,
    translateCategory,
    translateSubcategory,
    translateProductName,
    translateProductDescription,
    translateIngredient
  } = useLanguage();
  const translatedName = translateProductName(product.id, product.name);
  const translatedDescription = translateProductDescription(product.id, product.description);
  const [selections, setSelections] = useState<any>(initialItem?.selections || {
    toppings: [],
    aderezos: [],
    comentarios: ''
  });
  // Beverage state
  const [beverageOpen, setBeverageOpen] = useState(false);
  const [activeSubcategory, setActiveSubcategory] = useState<string | null>(null);
  const [selectedBeverage, setSelectedBeverage] = useState<Product | null>(null);
  // Group beverages by subcategory
  const beverageGroups = useMemo(() => {
    const groups: Record<string, Product[]> = {};
    MENU_PRODUCTS.filter(p => p.category === 'Bebidas').forEach(p => {
      const sub = p.subcategory || 'Otras';
      if (!groups[sub]) groups[sub] = [];
      groups[sub].push(p);
    });
    return groups;
  }, []);
  const handleToppingSelect = (option: ProductOption) => {
    const currentList = selections.toppings || [];
    const exists = currentList.find((item: ProductOption) => item.id === option.id);
    if (exists) {
      setSelections({
        ...selections,
        toppings: currentList.filter((item: ProductOption) => item.id !== option.id)
      });
    } else {
      if (isSandwichOrWrap && currentList.length >= 4) return;
      setSelections({
        ...selections,
        toppings: [...currentList, option]
      });
    }
  };
  const handleAderezoSelect = (option: ProductOption) => {
    const currentList = selections.aderezos || [];
    const exists = currentList.find((item: ProductOption) => item.id === option.id);
    if (exists) {
      setSelections({
        ...selections,
        aderezos: currentList.filter((item: ProductOption) => item.id !== option.id)
      });
    } else {
      if (isSandwichOrWrap && currentList.length >= 1) return;
      setSelections({
        ...selections,
        aderezos: [...currentList, option]
      });
    }
  };
  const calculateSubtotal = () => {
    let total = product.price;
    if (selectedBeverage) total += selectedBeverage.price;
    return total;
  };
  const handleSave = () => {
    const item: CartItemType = {
      id: initialItem?.id || `${product.id}_${Date.now()}`,
      productId: product.id,
      name: product.name,
      basePrice: product.price,
      quantity: initialItem?.quantity || 1,
      selections,
      subtotal: calculateSubtotal(),
      type: product.type || 'bowl'
    };
    // Add beverage as separate cart item
    if (selectedBeverage) {
      addItem({
        id: `${selectedBeverage.id}_${Date.now()}`,
        productId: selectedBeverage.id,
        name: selectedBeverage.name,
        basePrice: selectedBeverage.price,
        quantity: 1,
        selections: {},
        subtotal: selectedBeverage.price,
        type: 'bebida'
      });
    }
    onSave(item);
  };
  const isSaveValid = () => {
    if (isSandwichOrWrap) {
      return (selections.toppings?.length || 0) >= 1;
    }
    return true;
  };
  const toppingCount = selections.toppings?.length || 0;
  const aderezoCount = selections.aderezos?.length || 0;
  // Get all selected ingredients for visual progress
  const getAllSelectedIngredients = () => {
    const all: ProductOption[] = [];
    if (selections.toppings) all.push(...selections.toppings);
    if (selections.aderezos) all.push(...selections.aderezos);
    return all;
  };
  const selectedIngredients = getAllSelectedIngredients();
  const subtotalFormatted = formatPrice(calculateSubtotal());
  return <motion.div initial={{
    opacity: 0,
    y: 50
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: 50
  }} className="fixed inset-0 z-50 bg-carbon flex flex-col max-w-md mx-auto shadow-2xl" data-id="element-1166">
      <div className="bg-surface px-4 py-4 flex items-center justify-between shadow-sm z-20 sticky top-0 border-b border-gray-800" data-id="element-1167">
        <button onClick={onClose} className="p-2 -ml-2 text-gray-400 hover:text-white bg-surface/80 rounded-full backdrop-blur-sm" data-id="element-1168">
          <XIcon className="w-6 h-6" data-id="element-1169" />
        </button>
        <h2 className="font-heading font-semibold text-lg text-white tracking-wide" data-id="element-1170">
          {t('personalize')}
        </h2>
        <div className="w-10" data-id="element-1171"></div>
      </div>

      <div className="flex-1 overflow-y-auto pb-24" data-id="element-1172">
        {/* Hero Image */}
        {product.image && <div className="w-full h-48 relative bg-surface-light" data-id="element-1173">
            <img src={product.image} alt={translatedName} className="w-full h-full object-cover" loading="eager" data-id="element-1174" />
            <div className="absolute inset-0 bg-gradient-to-t from-carbon via-carbon/40 to-transparent" data-id="element-1175" />
            <div className="absolute bottom-4 left-4 right-4 text-white" data-id="element-1176">
              <h3 className="text-3xl font-heading font-bold mb-1 drop-shadow-md tracking-wide" data-id="element-1177">
                {translatedName}
              </h3>
              <p className="text-gray-300 text-sm drop-shadow line-clamp-2" data-id="element-1178">
                {translatedDescription}
              </p>
            </div>
          </div>}

        {/* Visual Progress Strip */}
        {isSandwichOrWrap && <div className="bg-surface/80 backdrop-blur-md border-b border-gray-800 py-3 px-4 z-10 shadow-sm sticky top-0" data-id="element-1179">
            <div className="flex items-center gap-3 overflow-x-auto hide-scrollbar pb-1 min-h-[60px]" data-id="element-1180">
              {selectedIngredients.length === 0 ? <p className="text-sm text-gray-500 italic w-full text-center" data-id="element-1181">
                  {t('yourIngredients')}
                </p> : <AnimatePresence data-id="element-1182">
                  {selectedIngredients.map(ing => <motion.div key={ing.id} initial={{
              opacity: 0,
              scale: 0.5
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.5
            }} className="flex flex-col items-center gap-1 flex-shrink-0" data-id="element-1183">
                      <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-verde shadow-sm bg-surface-light" data-id="element-1184">
                        {INGREDIENT_IMAGES[ing.id] ? <img src={INGREDIENT_IMAGES[ing.id]} alt={translateIngredient(ing.id, ing.name)} className="w-full h-full object-cover" loading="lazy" data-id="element-1185" /> : <div className="w-full h-full bg-verde/10 flex items-center justify-center text-verde font-bold text-xs" data-id="element-1186">
                            {translateIngredient(ing.id, ing.name).charAt(0)}
                          </div>}
                      </div>
                      <span className="text-[10px] text-gray-400 font-medium max-w-[56px] truncate text-center" data-id="element-1187">
                        {translateIngredient(ing.id, ing.name)}
                      </span>
                    </motion.div>)}
                </AnimatePresence>}
            </div>
          </div>}

        <div className="p-4" data-id="element-1188">
          {!product.image && <div className="mb-6" data-id="element-1189">
              <h3 className="text-2xl font-heading font-bold text-white mb-1 tracking-wide" data-id="element-1190">
                {translatedName}
              </h3>
              <p className="text-gray-400 text-sm" data-id="element-1191">{translatedDescription}</p>
            </div>}

          <p className="text-verde font-semibold text-lg mb-6 flex items-baseline gap-2" data-id="element-1192">
            {formatPrice(product.price).primary}
            <span className="text-sm text-gray-400 font-normal" data-id="element-1193">
              {formatPrice(product.price).secondary}
            </span>
          </p>

          {isSandwichOrWrap && <>
              {/* Toppings Section */}
              <div className="mb-8" data-id="element-1194">
                <div className="flex justify-between items-end mb-1" data-id="element-1195">
                  <h4 className="font-heading font-semibold text-lg text-white tracking-wide" data-id="element-1196">
                    {t('toppings')}
                  </h4>
                  <span className={`text-sm font-medium px-2.5 py-0.5 rounded-full ${toppingCount >= 1 ? 'bg-verde/10 text-verde' : 'bg-surface-light text-gray-400'}`} data-id="element-1197">
                    {toppingCount}/4
                  </span>
                </div>
                <p className="text-gray-500 text-xs mb-3" data-id="element-1198">
                  {t('chooseToppings')}
                </p>

                {toppingCount === 4 && <p className="text-xs text-verde bg-verde/10 px-3 py-1.5 rounded-lg mb-3 flex items-center gap-1 border border-verde/20" data-id="element-1199">
                    <CheckIcon className="w-3 h-3" data-id="element-1200" /> {t('maxReached')}
                  </p>}

                <div className="grid grid-cols-2 gap-2" data-id="element-1201">
                  {BOWL_OPTIONS.toppings.map(option => {
                const isSelected = selections.toppings?.some((t: ProductOption) => t.id === option.id);
                const isDisabled = !isSelected && toppingCount >= 4;
                return <button key={option.id} onClick={() => handleToppingSelect(option)} disabled={isDisabled} className={`p-2 rounded-xl border text-sm text-left transition-colors flex items-center gap-2 ${isSelected ? 'border-verde bg-verde/10 text-verde font-medium' : isDisabled ? 'border-gray-800 bg-surface-light text-gray-600 cursor-not-allowed' : 'border-gray-700 bg-surface text-gray-300 hover:border-gray-500'}`} data-id="element-1202">
                        {INGREDIENT_IMAGES[option.id] && <img src={INGREDIENT_IMAGES[option.id]} alt="" className={`w-8 h-8 rounded-full object-cover flex-shrink-0 ${isSelected ? 'ring-2 ring-verde ring-offset-1 ring-offset-surface' : ''}`} loading="lazy" data-id="element-1203" />}
                        <span className="leading-tight flex-1" data-id="element-1204">
                          {translateIngredient(option.id, option.name)}
                        </span>
                      </button>;
              })}
                </div>
              </div>

              {/* Aderezos Section */}
              <div className="mb-8" data-id="element-1205">
                <div className="flex justify-between items-end mb-1" data-id="element-1206">
                  <div className="flex items-center gap-2" data-id="element-1207">
                    <h4 className="font-heading font-semibold text-lg text-white tracking-wide" data-id="element-1208">
                      {t('dressing')}
                    </h4>
                    <span className="text-xs font-medium bg-surface-light text-gray-400 px-2 py-0.5 rounded-full border border-gray-700" data-id="element-1209">
                      {t('optional')}
                    </span>
                  </div>
                  <span className={`text-sm font-medium px-2.5 py-0.5 rounded-full ${aderezoCount >= 1 ? 'bg-verde/10 text-verde' : 'bg-surface-light text-gray-400'}`} data-id="element-1210">
                    {aderezoCount}/1
                  </span>
                </div>
                <p className="text-gray-500 text-xs mb-3" data-id="element-1211">
                  {t('chooseDressing')}
                </p>

                <div className="grid grid-cols-2 gap-2" data-id="element-1212">
                  {BOWL_OPTIONS.aderezos.map(option => {
                const isSelected = selections.aderezos?.some((a: ProductOption) => a.id === option.id);
                const isDisabled = !isSelected && aderezoCount >= 1;
                return <button key={option.id} onClick={() => handleAderezoSelect(option)} disabled={isDisabled} className={`p-2 rounded-xl border text-sm text-left transition-colors flex items-center gap-2 ${isSelected ? 'border-verde bg-verde/10 text-verde font-medium' : isDisabled ? 'border-gray-800 bg-surface-light text-gray-600 cursor-not-allowed' : 'border-gray-700 bg-surface text-gray-300 hover:border-gray-500'}`} data-id="element-1213">
                        {INGREDIENT_IMAGES[option.id] && <img src={INGREDIENT_IMAGES[option.id]} alt="" className={`w-8 h-8 rounded-full object-cover flex-shrink-0 ${isSelected ? 'ring-2 ring-verde ring-offset-1 ring-offset-surface' : ''}`} loading="lazy" data-id="element-1214" />}
                        <span className="leading-tight flex-1" data-id="element-1215">
                          {translateIngredient(option.id, option.name)}
                        </span>
                      </button>;
              })}
                </div>
              </div>

              {/* Beverage Accordion */}
              <div className="mb-8" data-id="element-1216">
                <button onClick={() => {
              setBeverageOpen(!beverageOpen);
              if (beverageOpen) setActiveSubcategory(null);
            }} className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${selectedBeverage ? 'border-verde bg-verde/10' : beverageOpen ? 'border-verde/50 bg-verde/5' : 'border-gray-700 bg-surface'}`} data-id="element-1217">
                  <div className="flex items-center gap-3" data-id="element-1218">
                    <span className="text-xl" data-id="element-1219">🥤</span>
                    <div className="text-left" data-id="element-1220">
                      <h4 className="font-heading font-semibold text-base text-white tracking-wide" data-id="element-1221">
                        {t('beverages')}
                      </h4>
                      {selectedBeverage ? <p className="text-xs text-verde font-medium" data-id="element-1222">
                          {translateProductName(selectedBeverage.id, selectedBeverage.name)}{' '}
                          · +{formatPrice(selectedBeverage.price).primary}
                        </p> : <p className="text-xs text-gray-400" data-id="element-1223">
                          {t('beverageOptional')}
                        </p>}
                    </div>
                  </div>
                  <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform ${beverageOpen ? 'rotate-180' : ''}`} data-id="element-1224" />
                </button>

                <AnimatePresence data-id="element-1225">
                  {beverageOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.2
              }} className="overflow-hidden" data-id="element-1226">
                      <div className="pt-3 space-y-2" data-id="element-1227">
                        {/* Subcategory buttons */}
                        {!activeSubcategory && <div className="grid grid-cols-2 gap-2" data-id="element-1228">
                            {Object.entries(beverageGroups).map(([subcat, products]) => <button key={subcat} onClick={() => setActiveSubcategory(subcat)} className="p-3 rounded-xl border border-gray-700 bg-surface text-left hover:border-gray-500 transition-colors flex items-center justify-between" data-id="element-1229">
                                  <div data-id="element-1230">
                                    <p className="text-sm font-medium text-gray-200" data-id="element-1231">
                                      {translateSubcategory(subcat)}
                                    </p>
                                    <p className="text-[11px] text-gray-500" data-id="element-1232">
                                      {products.length} {t('options')}
                                    </p>
                                  </div>
                                  <ChevronRightIcon className="w-4 h-4 text-gray-500" data-id="element-1233" />
                                </button>)}
                          </div>}

                        {/* Specific beverages within subcategory */}
                        {activeSubcategory && <div data-id="element-1234">
                            <button onClick={() => setActiveSubcategory(null)} className="text-sm text-verde font-medium mb-3 flex items-center gap-1" data-id="element-1235">
                              ← {translateSubcategory(activeSubcategory)}
                            </button>
                            <div className="grid grid-cols-2 gap-2" data-id="element-1236">
                              {beverageGroups[activeSubcategory]?.map(bev => {
                        const isSelected = selectedBeverage?.id === bev.id;
                        return <button key={bev.id} onClick={() => {
                          setSelectedBeverage(isSelected ? null : bev);
                          if (!isSelected) {
                            setActiveSubcategory(null);
                            setBeverageOpen(false);
                          }
                        }} className={`rounded-xl border overflow-hidden text-left transition-all ${isSelected ? 'border-verde ring-2 ring-verde/20 bg-verde/5' : 'border-gray-700 bg-surface hover:border-gray-500'}`} data-id="element-1237">
                                    {bev.image && <div className="h-20 w-full bg-surface-light relative" data-id="element-1238">
                                        <img src={bev.image} alt={translateProductName(bev.id, bev.name)} loading="lazy" className="w-full h-full object-cover" data-id="element-1239" />
                                        {isSelected && <div className="absolute top-1.5 right-1.5 bg-verde text-carbon rounded-full p-0.5" data-id="element-1240">
                                            <CheckIcon className="w-3 h-3" data-id="element-1241" />
                                          </div>}
                                      </div>}
                                    <div className="p-2" data-id="element-1242">
                                      <p className="text-[11px] font-medium text-gray-200 leading-tight line-clamp-2 min-h-[28px]" data-id="element-1243">
                                        {translateProductName(bev.id, bev.name)}
                                      </p>
                                      <p className={`text-[11px] font-semibold mt-1 ${isSelected ? 'text-verde' : 'text-gray-400'}`} data-id="element-1244">
                                        +{formatPrice(bev.price).primary}
                                      </p>
                                    </div>
                                  </button>;
                      })}
                            </div>
                          </div>}

                        {/* Selected beverage confirmation */}
                        {selectedBeverage && !activeSubcategory && <div className="flex items-center justify-between bg-verde/10 px-3 py-2.5 rounded-xl border border-verde/20" data-id="element-1245">
                            <div className="flex items-center gap-2" data-id="element-1246">
                              {selectedBeverage.image && <img src={selectedBeverage.image} alt="" className="w-8 h-8 rounded-lg object-cover" data-id="element-1247" />}
                              <div data-id="element-1248">
                                <p className="text-sm text-verde font-medium" data-id="element-1249">
                                  {translateProductName(selectedBeverage.id, selectedBeverage.name)}
                                </p>
                                <p className="text-[11px] text-verde/80" data-id="element-1250">
                                  +{formatPrice(selectedBeverage.price).primary}
                                </p>
                              </div>
                            </div>
                            <button onClick={() => setSelectedBeverage(null)} className="text-xs text-gray-300 bg-surface px-2.5 py-1 rounded-lg border border-gray-700 hover:text-white" data-id="element-1251">
                              {t('remove')}
                            </button>
                          </div>}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </>}

          {/* Comments Section */}
          <div className="mb-8" data-id="element-1252">
            <div className="flex items-center gap-2 mb-1" data-id="element-1253">
              <h4 className="font-heading font-semibold text-lg text-white tracking-wide" data-id="element-1254">
                {t('comments')}
              </h4>
              <span className="text-xs font-medium bg-surface-light text-gray-400 px-2 py-0.5 rounded-full border border-gray-700" data-id="element-1255">
                {t('optional')}
              </span>
            </div>
            <p className="text-gray-500 text-xs mb-3" data-id="element-1256">
              {t('specialInstructions')}
            </p>
            <textarea value={selections.comentarios} onChange={e => setSelections({
            ...selections,
            comentarios: e.target.value
          })} placeholder={t('commentsPlaceholder')} className="w-full h-24 p-4 bg-surface-light border border-gray-700 rounded-xl focus:ring-2 focus:ring-verde outline-none resize-none text-white placeholder-gray-500" data-id="element-1257" />
          </div>

          {/* Visual Preview — Stacked Ingredient Composition */}
          {isSandwichOrWrap && selectedIngredients.length > 0 && <div className="mb-6" data-id="element-1258">
              <h4 className="font-heading font-semibold text-lg mb-3 text-white tracking-wide" data-id="element-1259">
                {t('yourBowl').replace('Bowl', translatedName)}
              </h4>
              <div className="rounded-2xl overflow-hidden border border-gray-800 shadow-sm bg-surface-light relative" data-id="element-1260">
                {/* Stacked ingredients — layered composition */}
                <div className="relative w-full h-56 flex items-center justify-center" data-id="element-1261">
                  {/* Base bread/tortilla layer */}
                  {product.image && <div className="absolute inset-0 opacity-20" data-id="element-1262">
                      <img src={product.image} alt="" className="w-full h-full object-cover blur-sm" data-id="element-1263" />
                    </div>}
                  {/* Ingredient stack */}
                  <div className="relative w-52 h-52" data-id="element-1264">
                    <AnimatePresence data-id="element-1265">
                      {selectedIngredients.map((ing, idx) => {
                    const total = selectedIngredients.length;
                    const angle = idx / total * 360;
                    const radius = total <= 3 ? 40 : 55;
                    const x = Math.cos(angle * Math.PI / 180) * radius;
                    const y = Math.sin(angle * Math.PI / 180) * radius;
                    const size = total <= 2 ? 88 : total <= 4 ? 76 : 64;
                    const rotation = idx * 15 - 20;
                    return <motion.div key={ing.id} initial={{
                      opacity: 0,
                      scale: 0,
                      y: 30
                    }} animate={{
                      opacity: 1,
                      scale: 1,
                      y: 0
                    }} exit={{
                      opacity: 0,
                      scale: 0
                    }} transition={{
                      delay: idx * 0.08,
                      type: 'spring',
                      damping: 15
                    }} className="absolute rounded-full overflow-hidden shadow-xl border-2 border-gray-700" style={{
                      width: size,
                      height: size,
                      left: `calc(50% + ${x}px - ${size / 2}px)`,
                      top: `calc(50% + ${y}px - ${size / 2}px)`,
                      zIndex: idx + 1,
                      transform: `rotate(${rotation}deg)`
                    }} data-id="element-1266">
                            {INGREDIENT_IMAGES[ing.id] ? <img src={INGREDIENT_IMAGES[ing.id]} alt={translateIngredient(ing.id, ing.name)} className="w-full h-full object-cover" data-id="element-1267" /> : <div className="w-full h-full bg-surface flex items-center justify-center text-white font-bold" data-id="element-1268">
                                {translateIngredient(ing.id, ing.name).charAt(0)}
                              </div>}
                          </motion.div>;
                  })}
                    </AnimatePresence>
                  </div>
                </div>
                {/* Ingredient labels */}
                <div className="bg-surface px-4 py-3 border-t border-gray-800" data-id="element-1269">
                  <div className="flex flex-wrap gap-1.5 justify-center" data-id="element-1270">
                    {selectedIngredients.map(ing => <span key={ing.id} className="text-[11px] bg-surface-light text-gray-300 px-2.5 py-1 rounded-full font-medium border border-gray-700" data-id="element-1271">
                        {translateIngredient(ing.id, ing.name)}
                      </span>)}
                  </div>
                  <p className="text-xs text-gray-500 text-center mt-2" data-id="element-1272">
                    {toppingCount} {t('toppings').toLowerCase()}
                    {aderezoCount > 0 && ` · ${translateIngredient(selections.aderezos[0].id, selections.aderezos[0].name)}`}
                  </p>
                </div>
              </div>
            </div>}
        </div>
      </div>

      <div className="bg-surface border-t border-gray-800 p-4 pb-safe sticky bottom-0 z-20" data-id="element-1273">
        {!isSaveValid() && isSandwichOrWrap && <p className="text-center text-xs text-terracota mb-2 flex items-center justify-center gap-1" data-id="element-1274">
            <InfoIcon className="w-3 h-3" data-id="element-1275" />
            {t('chooseMin1')}
          </p>}
        <button onClick={handleSave} disabled={!isSaveValid()} className={`w-full py-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors min-h-[48px] border border-white/10 ${isSaveValid() ? 'bg-terracota text-white active:bg-[#9a3a18] shadow-lg shadow-terracota/20' : 'bg-gray-800 text-gray-500 cursor-not-allowed'}`} data-id="element-1276">
          {t('addToOrder')} — {subtotalFormatted.primary}
        </button>
      </div>
    </motion.div>;
}